/**
 * Single-source dispatcher for global keyboard shortcuts.
 *
 * Each shortcut handler is registered through this hook so we have one
 * authoritative list (also rendered by `KeyboardShortcutsPanel`). The
 * dispatcher skips when an editable element owns focus — typing in an
 * input box should never fire a shortcut.
 *
 * Supports:
 *  - simple combos: { key: 'k', mod: true } ⇒ Cmd-K / Ctrl-K
 *  - chord sequences: { sequence: ['g', 'f'] } ⇒ press g then f within 1s
 *  - plain keys: { key: '?' }
 */
import { useEffect, useRef } from 'react';
const CHORD_TIMEOUT_MS = 1000;
function isEditable(target) {
    if (!(target instanceof HTMLElement))
        return false;
    if (target.isContentEditable)
        return true;
    const tag = target.tagName;
    return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
}
function modPressed(e) {
    // Mac: Meta. Windows / Linux: Ctrl.
    return e.metaKey || e.ctrlKey;
}
export function useShortcuts(shortcuts) {
    const shortcutsRef = useRef(shortcuts);
    useEffect(() => {
        shortcutsRef.current = shortcuts;
    }, [shortcuts]);
    useEffect(() => {
        let chord = [];
        let chordTimer = null;
        const resetChord = () => {
            chord = [];
            if (chordTimer) {
                clearTimeout(chordTimer);
                chordTimer = null;
            }
        };
        const handler = (e) => {
            const editable = isEditable(e.target);
            const list = shortcutsRef.current;
            // Try simple combos first.
            for (const s of list) {
                if (!s.key)
                    continue;
                if (s.key !== e.key)
                    continue;
                if (s.mod && !modPressed(e))
                    continue;
                if (!s.mod && modPressed(e))
                    continue;
                if (s.shift && !e.shiftKey)
                    continue;
                if (!s.shift && e.shiftKey && s.key.length === 1)
                    continue;
                if (s.alt && !e.altKey)
                    continue;
                if (editable && !s.allowInEditable)
                    continue;
                e.preventDefault();
                resetChord();
                s.handler(e);
                return;
            }
            // Try chord sequences (no modifiers, single-char keys).
            if (modPressed(e) || e.altKey || e.shiftKey) {
                resetChord();
                return;
            }
            if (editable) {
                resetChord();
                return;
            }
            if (e.key.length !== 1) {
                resetChord();
                return;
            }
            chord.push(e.key.toLowerCase());
            if (chordTimer)
                clearTimeout(chordTimer);
            chordTimer = setTimeout(() => {
                chord = [];
            }, CHORD_TIMEOUT_MS);
            for (const s of list) {
                if (!s.sequence || s.sequence.length === 0)
                    continue;
                const seq = s.sequence.map((k) => k.toLowerCase());
                const tail = chord.slice(-seq.length);
                if (tail.length === seq.length && tail.every((k, i) => k === seq[i])) {
                    e.preventDefault();
                    resetChord();
                    s.handler(e);
                    return;
                }
            }
        };
        window.addEventListener('keydown', handler);
        return () => {
            window.removeEventListener('keydown', handler);
            if (chordTimer)
                clearTimeout(chordTimer);
        };
    }, []);
}
//# sourceMappingURL=useShortcuts.js.map